"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Clock, Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 18
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.45,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  const accreditations = [{
    name: "NASC",
    label: "National Access & Scaffolding Conf."
  }, {
    name: "CHAS",
    label: "Accredited Contractor"
  }];
  return <section id="request-a-quote" data-nav="dark" className="dark bg-background text-foreground relative py-20 md:py-28 overflow-hidden border-t border-border">
      {/* Decorative structural datum line */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="w-24 h-[3px] bg-primary" />
        </div>
      </div>

      {/* Subtle structural grid background overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(215_20%_24%/0.15)_1px,transparent_1px),linear-gradient(to_bottom,hsl(215_20%_24%/0.15)_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none opacity-40" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div {...animationProps} className="rounded-xl border border-border bg-card/60 backdrop-blur-sm p-8 sm:p-12 lg:p-16 shadow-2xl relative overflow-hidden">
          {/* Top amber highlight bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-transparent" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left Content Column */}
            <div className="lg:col-span-7 flex flex-col space-y-6 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider w-fit border border-border">
                <span className="size-2 rounded-full bg-primary animate-pulse" />
                Specialist Roofing Sub-Contract Support
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
                  Need reliable scaffolding for your next roofing project?
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground max-w-2xl font-normal leading-relaxed">
                  We supply planned access, temporary roofs, and edge protection across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East — keeping your roofers safe and your programme on schedule.
                </p>
              </div>

              {/* Direct dispatch contact block */}
              <div className="pt-2 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary shrink-0" />
                  <span>Monday to Friday, 8am – 6pm</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="size-4 text-primary shrink-0" />
                  <span>info@bmfscaffolding.co.uk</span>
                </div>
              </div>
            </div>

            {/* Right Action & Phone Column */}
            <div className="lg:col-span-5 flex flex-col justify-center gap-4 sm:gap-5 bg-secondary/40 p-6 sm:p-8 rounded-lg border border-border">
              <div className="space-y-1">
                <span className="text-xs uppercase font-bold tracking-wider text-muted-foreground">
                  Direct Dispatch & Estimating Desk
                </span>
                <div className="flex items-center gap-3">
                  <a href="tel:01344929900" className="text-2xl sm:text-3xl font-extrabold text-foreground hover:text-primary transition-colors flex items-center gap-2.5">
                    <Phone className="size-6 text-primary" />
                    01344 929900
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2">
                {/* Core Action Trio */}
                <Button asChild size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 shadow-sm text-base justify-center">
                  <Link to="/ContactRequestQuotePage">
                    Request a Quote
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button asChild variant="secondary" className="w-full h-11 border border-border bg-secondary hover:bg-secondary/80 text-secondary-foreground font-medium text-sm justify-center">
                    <a href="tel:01344929900">
                      <Phone className="size-4 mr-1.5 text-primary" />
                      Call Now
                    </a>
                  </Button>

                  <Button asChild variant="outline" className="w-full h-11 border-border text-foreground hover:bg-accent hover:text-accent-foreground font-medium text-sm justify-center">
                    <Link to="/ContactRequestQuotePage">
                      Discuss a Project
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="pt-2 text-xs text-muted-foreground text-center border-t border-border/80">
                Fast turnaround on site surveys and safety documentation
              </div>
            </div>
          </div>

          {/* Accreditation Footer Strip */}
          <div className="mt-10 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-4 text-center sm:text-left">
            {accreditations.map((item, i) => <div key={item.name} data-index={i} className="flex items-center justify-center sm:justify-start gap-3 p-2 rounded-md bg-secondary/20">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-bold text-foreground">
                    {item.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {item.label}
                  </div>
                </div>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}